/*
 * SPDX-License-Identifier: MIT
 * Copyright (c) 2024 Tech Inverge Solutions Pvt Ltd
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ShoppingBag, 
  Users, 
  Image as ImageIcon, 
  Mail, 
  ShoppingBasket,
  LogOut, 
  ChevronRight,
  Menu,
  X
} from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/admin',
      icon: LayoutDashboard,
      color: 'text-indigo-600 hover:text-indigo-700 bg-indigo-50/50'
    },
    {
      name: 'Products',
      path: '/admin/products',
      icon: ShoppingBasket,
      color: 'text-emerald-600 hover:text-emerald-700 bg-emerald-50/50'
    },
    {
      name: 'Orders',
      path: '/admin/orders',
      icon: ShoppingBag,
      color: 'text-blue-600 hover:text-blue-700 bg-blue-50/50'
    },
    {
      name: 'Customers',
      path: '/admin/users',
      icon: Users,
      color: 'text-pink-600 hover:text-pink-700 bg-pink-50/50'
    },
    {
      name: 'Banners',
      path: '/admin/banners',
      icon: ImageIcon,
      color: 'text-orange-600 hover:text-orange-700 bg-orange-50/50'
    },
    {
      name: 'Newsletter',
      path: '/admin/newsletter',
      icon: Mail,
      color: 'text-purple-600 hover:text-purple-700 bg-purple-50/50'
    }
  ];

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const username = 'Admin Manager';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      
      {/* Mobile Top Bar */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-20">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-lg shadow-md shadow-indigo-200">
            C
          </div>
          <span className="font-extrabold text-gray-900 tracking-wide text-lg bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">C-NINE Portal</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Sidebar - Desktop & Mobile Drawer */}
      <aside className={`
        fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 flex flex-col justify-between transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:h-screen
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div>
          {/* Logo / Header */}
          <div className="px-6 py-6 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-xl shadow-lg shadow-indigo-200 animate-pulse">
                C
              </div>
              <div>
                <span className="font-extrabold text-gray-900 tracking-wider text-xl bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent block">C-NINE</span>
                <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest -mt-1 block">Control Center</span>
              </div>
            </div>
            {/* Close button for mobile menu */}
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden p-1 rounded-md text-gray-400 hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="px-4 py-6 space-y-1.5">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`
                    w-full flex items-center justify-between px-4 py-3 text-sm font-semibold rounded-xl border transition-all duration-200 group
                    ${active 
                      ? 'bg-gradient-to-r from-blue-50 to-indigo-50/50 text-indigo-700 border-indigo-100/80 shadow-sm shadow-indigo-50/20' 
                      : 'bg-white text-gray-600 border-transparent hover:bg-gray-50 hover:text-gray-900 hover:border-gray-100/50'
                    }
                  `}
                >
                  <div className="flex items-center space-x-3.5">
                    <div className={`
                      p-1.5 rounded-lg border transition-all duration-200
                      ${active 
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-200' 
                        : 'bg-gray-50 text-gray-400 border-gray-100 group-hover:text-gray-500 group-hover:bg-gray-100/80'
                      }
                    `}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span>{item.name}</span>
                  </div>
                  <ChevronRight className={`
                    w-4 h-4 transition-transform duration-200
                    ${active ? 'text-indigo-600 translate-x-0.5' : 'text-gray-300 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5'}
                  `} />
                </button>
              );
            })}
          </nav>
        </div>

        {/* User profile & Logout */}
        <div className="p-4 border-t border-gray-100">
          <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100/50 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 border border-indigo-200 text-indigo-700 flex items-center justify-center font-bold text-sm">
                A
              </div>
              <div className="text-left">
                <span className="text-xs font-bold text-gray-800 block truncate w-32">{username}</span>
                <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider block">Administrator</span>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 bg-white hover:bg-red-50 text-gray-400 hover:text-red-600 border border-gray-100 hover:border-red-100 rounded-lg shadow-sm hover:shadow-red-50/20 transition-all duration-200"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

      </aside>

      {/* Overlay backdrop for mobile menu */}
      {mobileMenuOpen && (
        <div 
          onClick={() => setMobileMenuOpen(false)}
          className="fixed inset-0 z-10 bg-black/40 backdrop-blur-sm md:hidden"
        />
      )}

      {/* Main Content Area */}
      <main className="flex-grow min-h-screen md:h-screen md:overflow-y-auto z-0">
        {children}
      </main>

    </div>
  );
};

export default AdminLayout;
